import {useRef, useEffect, useState, useCallback} from "react";
import "../styles/Play.scss";
import {useLocation, useNavigate} from "react-router-dom";
import {path} from "src/router/path";
import {ARTIST, getArtistFromPath} from "src/utils/artist.utils";
import posterRiizeImage from "../assets/images/riize/poster_riize.png";
import posterAespaImage from "../assets/images/aespa/poster_aespa.png";
import {ConnectionState} from "../hooks/socket/socket.type";
import {
  AudioContextError,
  AUDIO_CONTEXT_ERROR_CODES,
} from "src/hooks/audioContext/audioContext.error";
import {useChat} from "../services/chat";
import {useTypingEffect} from "../services/chat/hooks/useTypingEffect";
import {
  RECORDING_MAX_TIME,
  RECORDING_WARNING_TIME,
} from "../services/chat/assets/constants/chatOptions";
import {ReactComponent as SendMessageIcon} from "../services/chat/assets/images/chat_send_icon.svg";
import {BackButton, Button, Timer, Toast} from "../components";
import {useTranslation} from "react-i18next";
import micPermissionIcon from "../assets/images/common/mic_off_icon.png";
import {getCharacterFromStorage} from "../services/chat/storage";
import {getBackgroundVideoUrl} from "../services/chat/assets/constants/characterVideoUrls";

// 개발환경 3분, 운영환경 30분
const CHAT_TIMER_WARNING_THRESHOLD_SECONDS = Number(
  process.env.REACT_APP_CHAT_TIMER_WARNING_THRESHOLD_SECONDS,
) as number;

// 개발환경 1분, 운영환경 10분
const WARNING_REMAINING_SECONDS = Number(
  process.env.REACT_APP_CHAT_WARNING_REMAINING_SECONDS,
) as number;

const Play = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const artist = getArtistFromPath(location.pathname);
  const {t, i18n} = useTranslation("common");

  const selectedCharacter = getCharacterFromStorage();
  const character = selectedCharacter?.identifier ?? "rizuko";

  const lang = (i18n.language === "en" ? "en" : "ko") as "ko" | "en";
  const characterGreeting =
    selectedCharacter?.information[lang]?.introduction ?? t("chat.greeting");

  const handleExpire = () => {
    navigate(path.expired(artist), {replace: true});
  };

  const handleBackButton = () => {
    navigate(path.character(artist), {replace: true});
  };

  const handleForegroundSessionNone = useCallback(() => {
    navigate(path.waiting(artist), {replace: true});
  }, [navigate, artist]);

  const handleServiceUnavailable = useCallback(() => {
    navigate(path.error(artist), {replace: true});
  }, [navigate, artist]);

  const handleEmergency = useCallback(() => {
    navigate(path.error(artist), {replace: true});
  }, [navigate, artist]);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const handleSocketError = useCallback(() => {
    showToast(t("chat.errorOccurred"));
  }, [showToast, t]);

  const handleNetworkDisconnect = useCallback(() => {
    showToast(t("chat.networkDisconnected"));
  }, [showToast, t]);

  const {
    messages,
    audioState,
    handleStartAudioRecording,
    handleStopAudioRecording,
    isProcessingAnswer,
    isProcessingQuestion,
    connectionState,
    isVoiceActivated,
    activateVoice,
    recordingTimeRemaining,
    inputValue,
    handleInputChange,
    handleSubmitInput,
    remainingSeconds,
  } = useChat({
    artist,
    character,
    language: i18n.language,
    onExpire: handleExpire,
    onForegroundSessionNone: handleForegroundSessionNone,
    onSocketError: handleSocketError,
    onNetworkTimeout: handleNetworkDisconnect,
    onServiceUnavailable: handleServiceUnavailable,
    onEmergency: handleEmergency,
  });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [recordingGuideBubblePhase, setRecordingGuideBubblePhase] = useState<
    "hidden" | "visible" | "exiting"
  >("hidden");

  const lastMessage = messages[messages.length - 1];
  const isLastAssistant = lastMessage?.role === "assistant";
  const completedMessages = isLastAssistant ? messages.slice(0, -1) : messages;
  const typingMessage = isLastAssistant ? lastMessage : null;
  const lastAssistantContent = typingMessage?.content ?? "";
  const displayedText = useTypingEffect(lastAssistantContent, {speedMs: 50});
  const isTyping =
    typingMessage !== null && displayedText !== lastAssistantContent;

  const isMicButtonDisabled =
    audioState === "processing" ||
    audioState === "starting" ||
    isProcessingAnswer ||
    isTyping ||
    inputValue.trim().length > 0;

  const handleInputClick = async () => {
    if (!isVoiceActivated) {
      try {
        await activateVoice();
      } catch (error) {
        console.error("Failed to activate audio:", error);
      }
    }
  };

  const handleMicButtonClick = () => {
    if (audioState === "recording") {
      handleStopAudioRecording();
    }
    if (audioState === "idle") {
      (async () => {
        try {
          await handleStartAudioRecording();
        } catch (e) {
          if (
            e instanceof AudioContextError &&
            e.code === AUDIO_CONTEXT_ERROR_CODES.NOT_ALLOWED
          ) {
            setShowMicPermissionModal(true);
          }
        }
      })();
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 0);
  }, [messages, isProcessingAnswer, isProcessingQuestion, displayedText]);

  useEffect(() => {
    if (remainingSeconds !== WARNING_REMAINING_SECONDS) {
      return;
    }
    showToast(
      t("chat.timeRemainingPopup.notice", {
        minutes: WARNING_REMAINING_SECONDS / 60,
      }),
    );
  }, [remainingSeconds, showToast, t]);

  useEffect(() => {
    if (audioState !== "recording" && audioState !== "starting") {
      setRecordingGuideBubblePhase((prev) =>
        prev !== "hidden" ? "exiting" : prev,
      );
      return;
    }
    setRecordingGuideBubblePhase("visible");
    const timer = window.setTimeout(() => {
      setRecordingGuideBubblePhase("exiting");
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [audioState]);

  const handleRecordingGuideBubbleAnimationEnd = (
    e: React.AnimationEvent<HTMLDivElement>,
  ) => {
    if (e.animationName === "fadeOut") {
      setRecordingGuideBubblePhase("hidden");
    }
  };

  return (
    <div className="play-container">
      <div className="chat-container">
        {connectionState === ConnectionState.DISCONNECTED && (
          <div className="chat-reconnect-banner">
            <p className="chat-reconnect-banner-text">
              {t("chat.networkDisconnected")}
            </p>
          </div>
        )}
        {toastMessage && (
          <Toast message={toastMessage} position={{bottom: 84}} />
        )}
        <div className="chat-header">
          <BackButton artist={artist} onClick={handleBackButton} />
          <Timer
            artist={artist}
            remainingSeconds={remainingSeconds}
            format="h:mm:ss"
            warningThresholdSeconds={CHAT_TIMER_WARNING_THRESHOLD_SECONDS}
          />
        </div>
        <div className="chat-wrapper">
          <div className="chat-background-container">
            <video
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              disablePictureInPicture
              preload="auto"
              poster={
                artist === ARTIST.aespa ? posterAespaImage : posterRiizeImage
              }
              className="chat-background-video"
            >
              <source src={getBackgroundVideoUrl(character)} type="video/mp4" />
            </video>
          </div>
          <div className="chat-messages-container" ref={messagesContainerRef}>
            <div className="chat-messages-spacer" />
            {completedMessages.map((message) => (
              <div
                key={message.ts}
                className={`chat-message ${message.role} ${artist}`}
              >
                <div className="chat-message-bubble">
                  <p className="chat-message-text">{message.content}</p>
                </div>
              </div>
            ))}
            {typingMessage && (
              <div
                key={typingMessage.ts}
                className={`chat-message assistant ${artist}`}
              >
                <div className="chat-message-bubble">
                  <p className="chat-message-text">{displayedText}</p>
                </div>
              </div>
            )}
            {isProcessingQuestion && (
              <div className={`chat-message user ${artist}`}>
                <div className="chat-message-bubble">
                  <div className="chat-processing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            {isProcessingAnswer && (
              <div className={`chat-message assistant ${artist}`}>
                <div className="chat-message-bubble">
                  <div className="chat-processing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div className={"chat-messages-bottom-spacer"} />
          </div>
          {messages.length === 0 && audioState === "idle" && (
            <div className="chat-welcome-message-wrapper">
              <div
                className={`chat-message chat-welcome-message assistant ${artist}`}
              >
                <div className="chat-welcome-message-bubble">
                  <p className="chat-message-text">{characterGreeting}</p>
                </div>
              </div>
            </div>
          )}
          <div className="chat-input-container">
            <div className="chat-mic-button-wrapper">
              {recordingGuideBubblePhase !== "hidden" && (
                <div
                  className={`chat-recording-guide-bubble ${recordingGuideBubblePhase === "exiting" ? "exiting" : ""}`}
                  role="status"
                  onAnimationEnd={handleRecordingGuideBubbleAnimationEnd}
                >
                  <p className="chat-recording-guide-bubble-text">
                    {t("chat.recordingGuideBubble")}
                  </p>
                </div>
              )}
              {(audioState === "recording" || audioState === "starting") && (
                <RecordingProgressRing
                  timeRemaining={recordingTimeRemaining}
                  artist={artist}
                />
              )}
              <button
                className={`chat-mic-button ${artist} ${
                  audioState === "recording" ||
                  audioState === "starting" ||
                  audioState === "processing" ||
                  isProcessingAnswer ||
                  isTyping ||
                  inputValue.trim().length > 0
                    ? ""
                    : "idle"
                }`}
                onPointerDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  if (!isMicButtonDisabled) {
                    handleMicButtonClick();
                  } else {
                    chatInputRef.current?.focus();
                  }
                }}
                onClick={handleMicButtonClick}
                disabled={isMicButtonDisabled}
              >
                <MicButtonIcon
                  state={
                    audioState as
                      | "recording"
                      | "processing"
                      | "starting"
                      | "idle"
                  }
                />
              </button>
            </div>
            <div className="chat-input-wrapper">
              <input
                ref={chatInputRef}
                type="text"
                className="chat-input"
                placeholder={t("chat.inputPlaceholder")}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSubmitInput();
                  }
                }}
                onClick={handleInputClick}
                autoComplete="off"
                maxLength={300}
                disabled={
                  audioState !== "idle" ||
                  isProcessingAnswer ||
                  isProcessingQuestion ||
                  isTyping
                }
              />
              <button
                className={`chat-send-message-button ${artist}`}
                onClick={handleSubmitInput}
                disabled={
                  !inputValue.trim() ||
                  isProcessingAnswer ||
                  isProcessingQuestion ||
                  isTyping
                }
              >
                <SendMessageIcon
                  className={`chat-send-message-icon ${artist}`}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>
        {showMicPermissionModal && (
          <div className="chat-mic-permission-overlay">
            <div className="chat-mic-permission-modal">
              <div className="chat-mic-permission-content">
                <div className="chat-mic-permission-icon-area">
                  <img
                    src={micPermissionIcon}
                    alt=""
                    className="chat-mic-permission-icon"
                  />
                </div>
                <p className="chat-mic-permission-title">
                  {t("chat.micPermissionPopup.title")}
                </p>
                <div className="chat-mic-permission-description-wrapper">
                  <p className="chat-mic-permission-description">
                    {t("chat.micPermissionPopup.description")}
                  </p>
                  <p className="chat-mic-permission-how-to-set">
                    {t("chat.micPermissionPopup.howToSet")}
                  </p>
                </div>
              </div>
              <Button
                artist={artist}
                label={t("chat.micPermissionPopup.button")}
                onClick={() => setShowMicPermissionModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Play;

const MicButtonIcon = ({state}: {state: string}) => {
  if (state === "recording" || state === "processing" || state === "starting") {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="4" y="4" width="16" height="16" fill="currentColor" rx="2" />
      </svg>
    );
  }

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z"
        fill="currentColor"
      />
      <path
        d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10H7V12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12V10H19Z"
        fill="currentColor"
      />
      <path d="M11 22H13V20H11V22Z" fill="currentColor" />
    </svg>
  );
};

type RecordingProgressRingProps = {
  timeRemaining: number;
  artist: string;
};

const RecordingProgressRing = ({
  timeRemaining,
  artist,
}: RecordingProgressRingProps) => {
  const progress =
    ((RECORDING_MAX_TIME - timeRemaining) / RECORDING_MAX_TIME) * 100;
  const isWarning = timeRemaining <= RECORDING_WARNING_TIME;

  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress / 100);

  return (
    <svg
      className={`chat-recording-progress-ring ${artist}`}
      width="64"
      height="64"
      viewBox="0 0 64 64"
    >
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        className="progress-ring-track"
        strokeWidth="3"
      />
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        className={isWarning ? "progress-ring-warning" : "progress-ring-normal"}
        strokeWidth="3"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform="rotate(-90 32 32)"
      />
    </svg>
  );
};
